<template>
  <div class="mt-4">
    <woot-card class="p-4">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <woot-input
          :label="$t('AI_SETTINGS.DATA_SYNC.LARK.API_HOST')"
          v-model.trim="form.lark_api_host"
        />
        <woot-input
          :label="$t('AI_SETTINGS.DATA_SYNC.LARK.APP_ID')"
          v-model.trim="form.lark_app_id"
        />
        <woot-input
          :label="$t('AI_SETTINGS.DATA_SYNC.LARK.APP_SECRET')"
          type="password"
          v-model.trim="form.lark_app_secret"
        />
        <woot-input
          :label="$t('AI_SETTINGS.DATA_SYNC.LARK.APP_TOKEN')"
          v-model.trim="form.lark_app_token"
        />
        <woot-input
          :label="$t('AI_SETTINGS.DATA_SYNC.LARK.TABLE_ID')"
          v-model.trim="form.lark_table_id"
        />
        <div class="flex items-center gap-4">
          <woot-switch v-model="form.lark_create_missing_fields" />
          <span>{{ $t('AI_SETTINGS.DATA_SYNC.LARK.CREATE_MISSING_FIELDS') }}</span>
        </div>
        <div class="flex items-center gap-4">
          <woot-switch v-model="form.lark_enable_attachments" />
          <span>{{ $t('AI_SETTINGS.DATA_SYNC.LARK.ENABLE_ATTACHMENTS') }}</span>
        </div>
      </div>

      <div class="mt-4 flex gap-2">
        <woot-button variant="smooth" color-scheme="ghost" size="small" :disabled="!canTest" :is-loading="isTesting" @click="onTest">
          {{ $t('AI_SETTINGS.DATA_SYNC.BUTTONS.TEST_CONNECTION') }}
        </woot-button>
      </div>
      <div class="mt-4 border-t border-white/10 pt-4">
        <div class="flex items-center justify-between mb-2">
          <span class="text-sm font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.LAYOUT.TITLE') }}</span>
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-xs text-body-light">
                <th class="px-2 py-1">#</th>
                <th class="px-2 py-1">Display name</th>
                <th class="px-2 py-1">Enabled</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(it, idx) in layoutItems" :key="it.internal_key" class="border-t border-white/10">
                <td class="px-2 py-1 w-10">{{ idx + 1 }}</td>
                <td class="px-2 py-1">
                  <input 
                    :value="it.display_name" 
                    @input="updateLayoutItem(idx, 'display_name', $event.target.value)"
                    class="input-base w-full" 
                  />
                </td>
                <td class="px-2 py-1">
                  <woot-switch 
                    :model-value="it.enabled" 
                    @input="updateLayoutItem(idx, 'enabled', $event)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </woot-card>
  </div>
</template>

<script>
import DataSyncConfigAPI from 'dashboard/api/dataSyncConfig';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'LarkBitableSyncCard',
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    layoutItems: {
      type: Array,
      default: () => []
    }
  },
  emits: ['config-change', 'layout-change'],
  data() {
    return {
      isTesting: false,
      form: {
        lark_api_host: '',
        lark_app_id: '',
        lark_app_secret: '',
        lark_app_token: '',
        lark_table_id: '',
        lark_create_missing_fields: true,
        lark_enable_attachments: false,
      },
    };
  },
  computed: {
    canTest() {
      const f = this.form;
      return (
        !!f.lark_api_host && !!f.lark_app_id && !!f.lark_app_secret &&
        !!f.lark_app_token && !!f.lark_table_id
      );
    },
  },
  watch: {
    config: {
      handler(newConfig) {
        if (newConfig) {
          this.form.lark_api_host = newConfig.lark_api_host || '';
          this.form.lark_app_id = newConfig.lark_app_id || '';
          this.form.lark_app_secret = newConfig.lark_app_secret || '';
          this.form.lark_app_token = newConfig.lark_app_token || '';
          this.form.lark_table_id = newConfig.lark_table_id || '';
          this.form.lark_create_missing_fields = !!newConfig.lark_create_missing_fields;
          this.form.lark_enable_attachments = !!newConfig.lark_enable_attachments;
        }
      },
      immediate: true
    },
    layoutItems: {
      handler() {
        // Layout items are managed by parent
      },
      immediate: true
    },
    form: {
      handler(newForm) {
        this.$emit('config-change', { ...newForm });
      },
      deep: true
    }
  },
  methods: {
    async onTest() {
      if (!this.canTest) return;
      
      // Emit current config for testing
      this.$emit('config-change', this.buildPayload());
      
      this.isTesting = true;
      try {
        const testPayload = this.buildPayload();
        const { data } = await DataSyncConfigAPI.testConnection(testPayload);
        if (data.success) {
          useAlert(this.$t('AI_SETTINGS.DATA_SYNC.CONNECTION_SUCCESS'), {
            type: 'success',
          });
        } else {
          useAlert(data.message || this.$t('GENERAL_ERROR'), {
            type: 'error',
          });
        }
      } catch (e) {
        const errorMessage = e?.response?.data?.error || e?.response?.data?.message || this.$t('GENERAL_ERROR');
        useAlert(errorMessage, { type: 'error' });
      } finally {
        this.isTesting = false;
      }
    },
    
    updateLayoutItem(index, field, value) {
      const updatedItems = [...this.layoutItems];
      updatedItems[index] = { ...updatedItems[index], [field]: value };
      this.$emit('layout-change', updatedItems);
    },
    buildPayload() {
      const f = this.form;
      return {
        target: 'lark_bitable',
        lark_api_host: f.lark_api_host,
        lark_app_id: f.lark_app_id,
        lark_app_secret: f.lark_app_secret,
        lark_app_token: f.lark_app_token,
        lark_table_id: f.lark_table_id,
        lark_create_missing_fields: f.lark_create_missing_fields,
        lark_enable_attachments: f.lark_enable_attachments,
      };
    },
  },
};
</script>


